/* eslint max-lines: ["error", {max: 260}] -- Due to cache tag additions */
import isEmpty from "lodash/isEmpty";

import { TasksStatusId, TaskStatusLabel } from "@/constants/Tasks";
import api, { CacheTagType } from "@/store/api";
import type { AppUserTaskCache } from "@/utils/api/tasks.util";
import { updateSelectedUserTasks } from "@/utils/api/tasks.util";

interface GetTasksRequest {
  patientId?: string;
}

interface UpdateTaskRequest {
  appUserId: string;
  isPatient: boolean;
  linkedPatients: LinkedPatientProfile[];
  selectedUserId: string;
  taskId: string;
  taskStatusId?: number;
}

interface ClearSurveyResultsRequest {
  linkedSurveyId: string;
}

interface CompleteSurveyTaskRequest {
  taskId: string;
}

interface LogWeightRequest {
  appUserId: string;
  isPatient: boolean;
  linkedPatients: LinkedPatientProfile[];
  observationDatetime: string;
  patientExternalId: string;
  selectedUserId: string;
  source: string;
  status: string;
  taskId: string;
  type: string;
  unit: string;
  value: number;
}
const GetTasksEndpoint = "getTasks";

enum TaskCompletionSource {
  Mobile = "Mobile",
}

/* eslint max-lines-per-function: ["error", {max: 220}] -- applies for entire file */
const tasksApi = api.injectEndpoints({
  endpoints: (build) => ({
    clearSurveyResults: build.mutation<string, ClearSurveyResultsRequest>({
      invalidatesTags: [CacheTagType.AssessmentLastUpdatedUsers],
      query: ({ linkedSurveyId }) => ({
        body: {
          jsonResults: JSON.stringify({}),
          status: TaskStatusLabel.InProgress,
          submittedSource: TaskCompletionSource.Mobile,
        },
        method: "PATCH",
        responseHandler: "text",
        url: `/assessments/family/${linkedSurveyId}`,
      }),
    }),
    completeSurveyTask: build.mutation<string, CompleteSurveyTaskRequest>({
      query: ({ taskId }) => ({
        body: {
          completionSource: TaskCompletionSource.Mobile,
          taskStatusId: TasksStatusId.Done,
        },
        method: "POST",
        responseHandler: "text",
        url: `/tasks/family/todo-list/${taskId}/mixpanel`,
      }),
    }),
    getAppUserTasks: build.query<TaskItem[], void>({
      query: () => ({
        method: "GET",
        url: "/tasks/family/todo-list",
      }),
      transformResponse: (response: TaskItem[]): TaskItem[] => response,
    }),
    getLastUpdatedUsers: build.query<LastUpdatedUserResponse[], string[]>({
      providesTags: [CacheTagType.AssessmentLastUpdatedUsers],
      query: (responseIds: string[]) => {
        const queryString = responseIds
          .map((responseId) => `response-id=${responseId}`)
          .join("&");
        return `/assessments/family/last-updated-users?${queryString}`;
      },
    }),
    getPatientTasks: build.query<TaskItem[], GetTasksRequest>({
      query: ({ patientId }) => ({
        method: "GET",
        url: `/tasks/family/patient/${patientId}/todo-list`,
      }),
      transformResponse: (response: TaskItem[]): TaskItem[] => response,
    }),
    [GetTasksEndpoint]: build.query<
      Record<string, TaskItem[]>,
      {
        appUserId: string;
        isPatient?: boolean;
        linkedPatients: LinkedPatientProfile[];
      }
    >({
      /* eslint-disable-next-line max-params, @typescript-eslint/naming-convention */
      queryFn: async (arg, { dispatch, forced }) => {
        const tasks: Record<string, TaskItem[]> = {};

        try {
          const appUserTasks = await dispatch(
            tasksApi.endpoints.getAppUserTasks.initiate(undefined, {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              forceRefetch: forced,
            }),
          ).unwrap();
          tasks[arg.appUserId] = appUserTasks;
        } catch {
          tasks[arg.appUserId] = [];
        }

        if (isEmpty(arg.linkedPatients) || arg.isPatient) {
          return { data: tasks };
        }
        await Promise.all(
          arg.linkedPatients.map(async (patient: LinkedPatientProfile) => {
            try {
              const patientTasks = await dispatch(
                tasksApi.endpoints.getPatientTasks.initiate(
                  {
                    patientId: patient.externalId,
                  },
                  {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    forceRefetch: forced,
                  },
                ),
              ).unwrap();
              tasks[patient.externalId] = patientTasks;
            } catch {
              tasks[patient.externalId] = [];
            }
          }),
        );

        return { data: tasks };
      },
    }),
    logWeight: build.mutation<string, LogWeightRequest>({
      // eslint-disable-next-line max-params
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;

        // once weight has been logged, invalidate the patient's weight observations cache
        await dispatch(
          api.util.invalidateTags([
            {
              id: arg.patientExternalId,
              type: CacheTagType.WeightObservations,
            },
          ]),
        );

        dispatch(
          tasksApi.util.updateQueryData(
            GetTasksEndpoint,
            {
              appUserId: arg.appUserId,
              isPatient: arg.isPatient,
              linkedPatients: arg.linkedPatients,
            },
            (oldCache: AppUserTaskCache) =>
              updateSelectedUserTasks({
                oldCache,
                selectedUserId: arg.selectedUserId,
                taskId: arg.taskId,
                taskStatusId: TasksStatusId.Done,
              }),
          ),
        );
      },
      query: ({ patientExternalId, ...request }) => ({
        body: {
          observationDatetime: request.observationDatetime,
          source: request.source,
          status: request.status,
          taskId: request.taskId,
          type: request.type,
          unit: request.unit,
          value: request.value,
        },
        method: "POST",
        responseHandler: "text",
        url: `/clinical-data/family/patients/${patientExternalId}/clinical-profile/observations`,
      }),
    }),
    updateTask: build.mutation<string, UpdateTaskRequest>({
      // eslint-disable-next-line max-params
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          tasksApi.util.updateQueryData(
            GetTasksEndpoint,
            {
              appUserId: arg.appUserId,
              isPatient: arg.isPatient,
              linkedPatients: arg.linkedPatients,
            },
            (oldCache: AppUserTaskCache) =>
              updateSelectedUserTasks({
                oldCache,
                selectedUserId: arg.selectedUserId,
                taskId: arg.taskId,
                taskStatusId: arg.taskStatusId || TasksStatusId.Done,
              }),
          ),
        );
      },
      query: ({ taskId, taskStatusId }) => ({
        body: {
          completionSource: TaskCompletionSource.Mobile,
          taskStatusId: taskStatusId || TasksStatusId.Done,
        },
        method: "PUT",
        responseHandler: "text",
        url: `/tasks/family/${taskId}`,
      }),
    }),
  }),
});

const {
  useClearSurveyResultsMutation,
  useCompleteSurveyTaskMutation,
  useGetLastUpdatedUsersQuery,
  useGetTasksQuery,
  useLogWeightMutation,
  useUpdateTaskMutation,
} = tasksApi;

export default tasksApi;

export {
  GetTasksEndpoint,
  useClearSurveyResultsMutation,
  useCompleteSurveyTaskMutation,
  useGetLastUpdatedUsersQuery,
  useGetTasksQuery,
  useLogWeightMutation,
  useUpdateTaskMutation,
};
